import React, { useState } from 'react'
import Button from 'components/button'
import ColourField from 'components/colour-field'
import Popover, { popoverPlacement } from 'components/popover'
import Toggle from 'components/toggle'
import { PanelRule, PanelSegmentSelect } from 'components/menu/panel'
import { Bold, Italic, Underline } from 'components/icons'
import { getDefaultFilterTypeForColType, isValidFilterTypeForColType } from 'components/spreadsheet/helpers/filtering'
import { IFilterType } from 'components/spreadsheet/types'
import { FilterTypesToDescription } from 'components/spreadsheet/constants/const'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import FilterValue from 'components/spreadsheet/components/menu/views/filter/FilterValue'
import { IContextMenuState } from 'types'
import { IColourItemProps } from '.'
import { styleToggle } from './styles'

// A conditional formatting rule, as a pill like a filter's: the columns it colours, the condition and value a row has
// to meet, and then a preview of the style (an "Aa" in the rule's own colours and type) that opens
// the style itself.

interface StyleProps {
  backgroundColour: string
  fontColour: string
  fontBold: boolean
  fontItalic: boolean
  fontUnderline: boolean
  applyToEntireRow: boolean
}

interface StyleSegmentProps extends StyleProps {
  onChange: (style: Partial<StyleProps>) => void
}

/** The rule's style: a preview of it in the pill, opening a popover to set its colours and type. */
const StyleSegment: React.FC<StyleSegmentProps> = ({ onChange, ...style }) => {
  const [at, setAt] = useState<IContextMenuState | null>(null)
  const toggle = (key: 'fontBold' | 'fontItalic' | 'fontUnderline', label: string, icon: React.ReactNode) => (
    <Button
      internalType="transparent"
      className={styleToggle(style[key])}
      aria-pressed={style[key]}
      aria-label={label}
      onClick={() => onChange({ [key]: !style[key] })}
    >
      {icon}
    </Button>
  )

  return (
    <>
      <Button
        internalType="transparent"
        className="h-full shrink-0 rounded-none border-0 px-1.5 hover:bg-surface-hover focus:shadow-none! focus:transform-none!"
        aria-label="Style"
        aria-haspopup="dialog"
        aria-expanded={at !== null}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setAt(popoverPlacement(event.currentTarget.getBoundingClientRect(), { align: 'start', height: 220 }))
        }
      >
        <span
          className="flex h-5 min-w-7 items-center justify-center rounded-[5px] border border-black/10 px-1 text-[12px] leading-none"
          style={{
            backgroundColor: style.backgroundColour,
            color: style.fontColour,
            fontWeight: style.fontBold ? 700 : 500,
            fontStyle: style.fontItalic ? 'italic' : 'normal',
            textDecoration: style.fontUnderline ? 'underline' : 'none'
          }}
        >
          Aa
        </span>
      </Button>
      <Popover id="colour-style" at={at} onClose={() => setAt(null)} nested label="Style" title="Style">
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-body">Background</span>
            <ColourField
              label="Background colour"
              value={style.backgroundColour}
              onSave={async (backgroundColour) => {
                onChange({ backgroundColour })
                return true
              }}
              confirm={false}
              nested
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-body">Text</span>
            <ColourField
              label="Text colour"
              value={style.fontColour}
              onSave={async (fontColour) => {
                onChange({ fontColour })
                return true
              }}
              confirm={false}
              nested
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-body">Type</span>
            <div className="flex items-center gap-1">
              {toggle('fontBold', 'Bold', <Bold />)}
              {toggle('fontItalic', 'Italic', <Italic />)}
              {toggle('fontUnderline', 'Underline', <Underline />)}
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-body">Apply to the whole row</span>
            <Toggle
              aria-label="Apply to the whole row"
              toggled={style.applyToEntireRow}
              onToggle={(applyToEntireRow) => onChange({ applyToEntireRow })}
            />
          </div>
        </div>
      </Popover>
    </>
  )
}

interface ColourItemProps extends IColourItemProps {
  onDelete: (index: number) => void
  onChange: (newColour: IColourItemProps, prevColour: IColourItemProps) => void
  index: number
  optionsOpen?: boolean
}

const ColourItem: React.FC<ColourItemProps> = ({
  columnId,
  columnName,
  filterType,
  value,
  multipleValues,
  backgroundColour,
  fontColour,
  fontBold,
  fontItalic,
  fontUnderline,
  applyToEntireRow,
  selectedColumns,
  columnOptions,
  kind,
  column,
  index,
  focus,
  onDelete,
  onChange,
  optionsOpen
}) => {
  const computeTypesOptions = () => {
    // 0.  Early out ­– nothing selected yet
    if (selectedColumns.length === 0) return []

    // 1.  Convenience: every possible filter-type once
    const allFilterTypes = Object.values(IFilterType)

    // 2.  Build the intersection: start with “all”, then keep only
    //     the types that are valid for every selected column’s kind
    const commonTypes = selectedColumns.reduce<Set<IFilterType>>((acc, { value }) => {
      // kind of *this* column
      const col = columnOptions.find((c) => c.value === value)
      if (!col) {
        // Column isn’t in the current view – ignore it
        return acc
      }

      // all filter types that work for that kind
      const validForCol = new Set(allFilterTypes.filter((t) => isValidFilterTypeForColType(col.kind, t)))

      // first column → initialise acc
      if (acc.size === 0) return validForCol

      // subsequent columns → intersection
      return new Set([...acc].filter((t) => validForCol.has(t)))
    }, new Set())

    // 3.  Turn the Set into the shape <Select> expects
    return [...commonTypes].map((type) => ({
      value: type,
      label: FilterTypesToDescription[type]
    }))
  }
  const typesOptions = computeTypesOptions()

  const handleColumnChange = (selectedColumnId: string) => {
    /* -----------------------------------------------------------
     * A. Build the new column-selection list
     * ----------------------------------------------------------- */
    const foundSelectedColumn = selectedColumns.find((c) => c.value === selectedColumnId)
    const newSelectedColumns = foundSelectedColumn
      ? selectedColumns.filter((c) => c.value !== selectedColumnId) // toggle off
      : [
          ...selectedColumns,
          {
            value: selectedColumnId,
            label: columnOptions.find((c) => c.value === selectedColumnId)?.label ?? ''
          }
        ] // toggle on

    newSelectedColumns.sort((a, b) => a.label.localeCompare(b.label))

    /* -----------------------------------------------------------
     * B. Find the intersection of valid filter-types
     * ----------------------------------------------------------- */
    const allFilterTypes = Object.values(IFilterType)
    const commonTypes = newSelectedColumns.reduce<Set<IFilterType>>((acc, { value }) => {
      const col = columnOptions.find((o) => o.value === value)
      if (!col) {
        // Column isn’t in the current view – ignore it
        return acc
      }
      const validForCol = new Set(allFilterTypes.filter((t) => isValidFilterTypeForColType(col.kind, t)))
      return acc.size === 0 ? validForCol : new Set([...acc].filter((t) => validForCol.has(t)))
    }, new Set())

    /* -----------------------------------------------------------
     * C. Decide what the *new* filter-type should be
     * ----------------------------------------------------------- */
    let nextFilterType = filterType
    let nextValue = value
    let nextMultiple = multipleValues

    if (!commonTypes.has(filterType)) {
      // pick the default of the **most recently clicked** column
      const clickedCol = columnOptions.find((o) => o.value === selectedColumnId)
      if (!clickedCol) return
      const clickedKind = clickedCol.kind
      nextFilterType = getDefaultFilterTypeForColType(clickedKind)

      // clear any user-entered value(s)
      nextValue = ''
      nextMultiple = []
    }

    /* -----------------------------------------------------------
     * D. Bubble the change up
     * ----------------------------------------------------------- */
    onChange(
      {
        columnId,
        columnName,
        filterType: nextFilterType,
        value: nextValue,
        multipleValues: nextMultiple,
        backgroundColour,
        fontColour,
        fontBold,
        fontItalic,
        fontUnderline,
        applyToEntireRow,
        selectedColumns: newSelectedColumns,
        kind,
        column,
        columnOptions,
        focus
      },
      {
        columnId,
        columnName,
        filterType,
        value,
        multipleValues,
        backgroundColour,
        fontColour,
        fontBold,
        fontItalic,
        fontUnderline,
        applyToEntireRow,
        selectedColumns,
        kind,
        column,
        columnOptions,
        focus
      }
    )
  }

  const handleFilterTypeChange = (newFilterType: IFilterType) => {
    if (newFilterType === IFilterType.one_of || newFilterType === IFilterType.not_one_of) {
      onChange({ ...prevColour, filterType: newFilterType, multipleValues: multipleValues || [] }, prevColour)
    } else if (value !== '') {
      onChange({ ...prevColour, filterType: newFilterType, value: value, multipleValues: [] }, prevColour)
    } else if (
      newFilterType === IFilterType.is_null ||
      newFilterType === IFilterType.is_not_null ||
      newFilterType === IFilterType.is_valid ||
      newFilterType === IFilterType.is_not_valid
    ) {
      onChange({ ...prevColour, filterType: newFilterType, value: '' }, prevColour)
    } else {
      onChange({ ...prevColour, filterType: newFilterType }, prevColour)
    }
  }
  const prevColour: IColourItemProps = {
    columnId,
    columnName,
    filterType,
    value,
    multipleValues,
    backgroundColour,
    fontColour,
    fontBold,
    fontItalic,
    fontUnderline,
    applyToEntireRow,
    selectedColumns,
    kind,
    column,
    columnOptions,
    focus
  }
  const selectedColumnColoursFiltered = selectedColumns.filter(
    (selectedColumn) => !!columnOptions.find((columnOption) => columnOption.value === selectedColumn.value)
  )

  if (typesOptions.length === 0 || selectedColumnColoursFiltered.length === 0)
    // If no valid filter types or no selected columns, don't render the item
    return null

  return (
    <PanelRule
      id={`colour-${index}`}
      removeLabel={`Remove colour rule on ${selectedColumnColoursFiltered.map((c) => c.label).join(', ')}`}
      onRemove={() => onDelete(index)}
      data-testid="colour-item"
    >
      <PanelSegmentSelect
        ariaLabel="Columns"
        tone="field"
        multiselect
        options={columnOptions.map((option) => ({ ...option, icon: <IconSelector kind={option.kind} /> }))}
        selected={selectedColumnColoursFiltered.map((c) => c.value)}
        onPick={handleColumnChange}
        menuWidth={240}
      />
      <PanelSegmentSelect
        ariaLabel="Condition"
        tone="muted"
        options={typesOptions}
        selected={filterType ? [filterType] : []}
        onPick={(option) => handleFilterTypeChange(option as IFilterType)}
        searchable={false}
        menuWidth={160}
      />
      <FilterValue
        column={column}
        filterType={filterType}
        value={value}
        multipleValues={multipleValues || []}
        onChange={(newValue: string) => onChange({ ...prevColour, value: newValue }, prevColour)}
        onChangeMultipleValues={(values: string[]) =>
          onChange({ ...prevColour, multipleValues: values || [] }, prevColour)
        }
        optionsOpen={optionsOpen}
      />
      <StyleSegment
        backgroundColour={backgroundColour}
        fontColour={fontColour}
        fontBold={!!fontBold}
        fontItalic={!!fontItalic}
        fontUnderline={!!fontUnderline}
        applyToEntireRow={!!applyToEntireRow}
        onChange={(style) => onChange({ ...prevColour, ...style }, prevColour)}
      />
    </PanelRule>
  )
}

export default ColourItem
